import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { CouponCodeInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getCouponCodesColumns } from './CouponCodesColumns';
import { CouponCodesUpdateDialog } from './CouponCodesUpdateDialog';

const CouponCodesAdminList: React.FC = () => {
    const [codes, setCodes] = useState<CouponCodeInterface[]>([]);
    const [singleCode, setSingleCode] = useState<CouponCodeInterface | null>(null);
    const [codeId, setCodeId] = useState('');
    const [codeString, setCodeString] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [codeToDelete, setCodeToDelete] = useState<CouponCodeInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [codeToUpdate, setCodeToUpdate] = useState<CouponCodeInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);

    // Fetch all codes
    const handleGetAllCodes = async () => {
        setError(null);
        setSingleCode(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<CouponCodeInterface[]>>('/couponCodes');
            if (result.status && result.data) {
                setCodes(result.data);
            } else {
                setError(result.message || 'Failed to fetch codes');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch codes');
        }
    };

    // Fetch code by id
    const handleGetCodeById = async () => {
        setError(null);
        setCodes([]);
        setSuccess(null);
        if (!codeId) return setError('Please enter a Code ID');
        try {
            const result = await apiGet<GenericResponseInterface<CouponCodeInterface>>(`/couponCodes/${codeId}`);
            if (result.status && result.data) {
                setSingleCode(result.data);
            } else {
                setError(result.message || 'Failed to fetch code');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch code');
        }
    };

    // Fetch code by code string
    const handleGetCodeByString = async () => {
        setError(null);
        setCodes([]);
        setSuccess(null);
        if (!codeString) return setError('Please enter a Code string');
        try {
            const result = await apiGet<GenericResponseInterface<CouponCodeInterface>>(`/couponCodes/code/${codeString}`);
            if (result.status && result.data) {
                setSingleCode(result.data);
            } else {
                setError(result.message || 'Failed to fetch code');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch code');
        }
    };

    // Delete code
    const handleDeleteCode = async () => {
        if (!codeToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/couponCodes/delete/${codeToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Code deleted successfully');
                setDeleteDialogOpen(false);
                setCodeToDelete(null);
                handleGetAllCodes();
            } else {
                setError(result.message || 'Failed to delete code');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete code');
        }
    };

    // Update code
    const handleUpdateCode = async () => {
        if (!codeToUpdate || !codeToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);

        try {
            const result = await apiRequest<GenericResponseInterface<CouponCodeInterface>>(
                `/couponCodes/update/${codeToUpdate.id}`,
                'PUT',
                codeToUpdate
            );
            if (result.status) {
                setSuccess(result.message || 'Code updated successfully');
                setUpdateDialogOpen(false);
                setCodeToUpdate(null);
                handleGetAllCodes();
            } else {
                setUpdateError(result.message || 'Failed to update code');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update code');
        }
    };

    const columns = getCouponCodesColumns(
        setCodeToDelete,
        setDeleteDialogOpen,
        setCodeToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllCodes();
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleGetAllCodes}>Get All Codes</Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        label="Code ID"
                        value={codeId}
                        onChange={e => setCodeId(e.target.value)}
                        size="small"
                    />
                    <Button variant="contained" onClick={handleGetCodeById}>Get by ID</Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        label="Code String"
                        value={codeString}
                        onChange={e => setCodeString(e.target.value)}
                        size="small"
                    />
                    <Button variant="contained" onClick={handleGetCodeByString}>Get by Code</Button>
                </Box>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {codes.length > 0 && (
                    <DataGrid
                        rows={codes.map(c => ({ ...c, id: c.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {singleCode && (
                    <DataGrid
                        rows={[{ ...singleCode, id: singleCode.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this coupon code?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteCode}>Delete</Button>
                </DialogActions>
            </Dialog>
            
            <CouponCodesUpdateDialog
                open={updateDialogOpen}
                codeToUpdate={codeToUpdate}
                setCodeToUpdate={setCodeToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdateCode}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default CouponCodesAdminList;
